<template>
  <Transition name="banner">
    <div
      v-if="isReadOnly"
      class="read-only-banner"
      role="alert"
      data-testid="read-only-banner"
    >
      <div class="read-only-banner__content">
        <span class="read-only-banner__icon" aria-hidden="true">&#9888;</span>
        <div class="read-only-banner__text">
          <p class="read-only-banner__title">{{ t('organization.readOnly.title') }}</p>
          <p class="read-only-banner__detail">
            {{ reasonMessage }} {{ t('organization.readOnly.readingStillWorks') }}
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
/**
 * Tells a practice that its organisation refuses writes, and why (#4445).
 *
 * Persistent and undismissable on purpose: read-only is not an event that
 * happened once, it is the state every save on every screen will keep hitting
 * until Scrivio lifts it. That is also why the refusal raises no toast —
 * `SUPPRESSED_ERROR_MESSAGE` silences the per-action message so this banner is
 * the one place the condition is stated.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOrganizationReadOnly } from '@/composables/useOrganizationReadOnly'
import type { OrganizationReadOnlyReason } from '@/utils/errorHandling'

const { t } = useI18n()
const { isReadOnly, reason } = useOrganizationReadOnly()

/**
 * The sentence per reason, written out rather than interpolated into the key,
 * so every reason this build can name is greppable and a reason it cannot name
 * falls back to wording that is true of both.
 */
const REASON_KEYS: Record<OrganizationReadOnlyReason, string> = {
  SUSPENDED: 'organization.readOnly.reason.suspended',
  UNPAID: 'organization.readOnly.reason.unpaid',
  CANCELED: 'organization.readOnly.reason.canceled',
}

const reasonMessage = computed(() =>
  t(reason.value ? REASON_KEYS[reason.value] : 'organization.readOnly.reason.unknown')
)
</script>

<style scoped>
.read-only-banner {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  /* Solid fill under `--color-text-inverse`, as the impersonation banner uses:
     this is the one thing telling a practice its writes are being refused. */
  background: var(--color-semantic-warning-strong);
  color: var(--color-text-inverse);
}

.read-only-banner__content {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.read-only-banner__icon {
  font-size: var(--font-size-lg);
}

.read-only-banner__text {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
}

.read-only-banner__title {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.read-only-banner__detail {
  margin: 0;
  font-size: var(--font-size-sm);
}

/* Transition animations */
.banner-enter-active,
.banner-leave-active {
  transition:
    opacity var(--transition-duration-normal) var(--transition-easing-ease-out),
    transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
